import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { CompanyContextBanner } from '~/components/domain/CompanyContextBanner';
import { useToast } from '~/components/feedback';
import { Button, DatePicker, Input, Select, Textarea } from '~/components/primitives';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { type PaymentUpdateBody, fetchPayment, updatePayment } from '~/lib/api/payments';
import shared from './_auth._shared.module.css';

/**
 * EFE-7 — Payment edit (draft only). Server enforces state guard.
 */
export const Route = createFileRoute('/_auth/payments/$id/edit')({
  component: PaymentEdit,
});

function PaymentEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['payments', id],
    queryFn: () => fetchPayment(id),
  });

  type Currency = 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY';
  const [form, setForm] = useState({
    payment_date: '',
    amount_value: '',
    amount_currency: 'KZT' as Currency,
    method: 'bank_transfer' as 'bank_transfer' | 'cash' | 'card' | 'offset' | 'other',
    notes_ru: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const p = query.data;
      setForm({
        payment_date: p.payment_date ?? '',
        amount_value: p.amount?.amount ?? '0',
        amount_currency: (p.amount?.currency ?? 'KZT') as Currency,
        method: (p.method ?? 'bank_transfer') as typeof form.method,
        notes_ru: p.notes_ru ?? '',
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () => {
      const p = query.data;
      const body: PaymentUpdateBody = {
        payment_date: form.payment_date,
        amount: { amount: form.amount_value, currency: form.amount_currency },
        method: form.method,
        notes_ru: form.notes_ru || null,
        // EFE-14: optimistic concurrency.
        expected_updated_at: p?.updated_at ?? p?.created_at,
      };
      return updatePayment(id, body);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.payment_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['payments', id] });
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void navigate({ to: '/payments/$id', params: { id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.payment_toast_update_failed(),
        description: renderApiErrorMessage(err),
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const p = query.data;
  const readOnly = p.state !== 'draft';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_payments_title(), to: '/payments' },
              { label: p.payment_date, to: `/payments/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={m.payment_edit_title()}
      />
      {/* access-control (F9) — контекст-плашка компании на edit-форме (консистентно с new/detail). */}
      <CompanyContextBanner />
      {readOnly && (
        <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
          {m.error_entity_not_editable({
            current_state: p.state,
            editable_states: 'draft',
          })}
        </div>
      )}
      <div className={shared.formColumn480}>
        <DatePicker
          label={m.payment_field_payment_date()}
          value={form.payment_date || null}
          onChange={(next) => setForm({ ...form, payment_date: next ?? '' })}
          disabled={readOnly}
        />
        <div className={shared.formGrid2}>
          <Input
            label={m.payment_field_amount()}
            value={form.amount_value}
            onChange={(e) => setForm({ ...form, amount_value: e.target.value })}
            disabled={readOnly}
            inputMode="decimal"
          />
          <Select
            label={m.payment_field_currency()}
            value={form.amount_currency}
            onChange={(e) => setForm({ ...form, amount_currency: e.target.value as Currency })}
            disabled={readOnly}
            options={[
              { value: 'KZT', label: 'KZT' },
              { value: 'USD', label: 'USD' },
              { value: 'EUR', label: 'EUR' },
              { value: 'RUB', label: 'RUB' },
              { value: 'CNY', label: 'CNY' },
            ]}
          />
        </div>
        <Select
          label={m.payment_field_method()}
          value={form.method}
          onChange={(e) => setForm({ ...form, method: e.target.value as typeof form.method })}
          disabled={readOnly}
          options={[
            { value: 'bank_transfer', label: m.payment_method_bank_transfer() },
            { value: 'cash', label: m.payment_method_cash() },
            { value: 'card', label: m.payment_method_card() },
            { value: 'offset', label: m.payment_method_offset() },
            { value: 'other', label: m.payment_method_other() },
          ]}
        />
        <Textarea
          label={m.payment_field_notes()}
          value={form.notes_ru}
          onChange={(e) => setForm({ ...form, notes_ru: e.target.value })}
          disabled={readOnly}
          rows={3}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/payments/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={readOnly}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
